import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { memoAPI } from '../services/memoApi';
import { transactionAPI } from '../services/api';
import {
    ArrowLeft,
    Plus,
    Edit,
    Trash2,
    FileText,
    Calendar,
    User,
    Tag
} from 'lucide-react';
import { formatDate, formatDateTime } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { useToast } from '../contexts/ToastContext';

const MemoDetail = () => {
    const { id: transactionId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingMemo, setEditingMemo] = useState(null);
    const [formData, setFormData] = useState({
        category: '계약',
        content: '',
        is_important: false
    });

    // 계약 정보 조회
    const { data: contractData } = useQuery({
        queryKey: ['contract', transactionId],
        queryFn: () => transactionAPI.getById(transactionId),
        select: (response) => response.data,
        enabled: !!transactionId, // transactionId가 있을 때만 실행
    });

    // 메모 목록 조회
    const { data: memoData, isLoading, error } = useQuery({
        queryKey: ['memos', transactionId],
        queryFn: () => memoAPI.getByTransaction(transactionId),
        select: (response) => {
            console.log('메모 목록 조회 응답:', response);
            console.log('response.data:', response.data);
            console.log('response.data.data:', response.data?.data);
            return response.data.data; // memos와 transaction이 포함된 객체 반환
        },
        enabled: !!transactionId, // transactionId가 있을 때만 실행
    });

    // 메모 생성 뮤테이션
    const createMutation = useMutation({
        mutationFn: memoAPI.create,
        onSuccess: (response) => {
            console.log('메모 생성 성공 응답:', response);
            queryClient.invalidateQueries(['memos', transactionId]);
            setIsCreateModalOpen(false);
            setFormData({ category: '계약', content: '', is_important: false });
            showToast('메모가 생성되었습니다.', 'success');
        },
        onError: (error) => {
            console.error('메모 생성 실패:', error);
            showToast(`메모 생성 실패: ${error.response?.data?.message || error.message}`, 'error');
        }
    });

    // 메모 수정 뮤테이션
    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => memoAPI.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['memos', transactionId]);
            setIsEditModalOpen(false);
            setEditingMemo(null);
            showToast('메모가 수정되었습니다.', 'success');
        },
        onError: (error) => {
            showToast(`메모 수정 실패: ${error.response?.data?.message || error.message}`, 'error');
        }
    });

    // 메모 삭제 뮤테이션
    const deleteMutation = useMutation({
        mutationFn: memoAPI.delete,
        onSuccess: () => {
            queryClient.invalidateQueries(['memos', transactionId]);
            showToast('메모가 삭제되었습니다.', 'success');
        },
        onError: (error) => {
            showToast(`메모 삭제 실패: ${error.response?.data?.message || error.message}`, 'error');
        }
    });

    // transactionId가 변경될 때마다 상태 초기화
    useEffect(() => {
        if (transactionId) {
            setFormData({ category: '계약', content: '', is_important: false });
            setIsCreateModalOpen(false);
            setIsEditModalOpen(false);
            setEditingMemo(null);
        }
    }, [transactionId]);

    const handleCreate = () => {
        if (!formData.content.trim()) {
            showToast('메모 내용을 입력해주세요.', 'error');
            return;
        }

        createMutation.mutate({
            transaction_id: parseInt(transactionId),
            category: formData.category,
            content: formData.content.trim(),
            is_important: formData.is_important || false
        });
    };

    const handleEdit = (memo) => {
        setEditingMemo(memo);
        setFormData({
            category: memo.category,
            content: memo.content,
            is_important: memo.is_important || false
        });
        setIsEditModalOpen(true);
    };

    const handleUpdate = () => {
        if (!formData.content.trim()) {
            showToast('메모 내용을 입력해주세요.', 'error');
            return;
        }

        updateMutation.mutate({
            id: editingMemo.id,
            data: {
                category: formData.category,
                content: formData.content.trim(),
                is_important: formData.is_important || false
            }
        });
    };

    const handleDelete = (memo) => {
        if (window.confirm('정말로 이 메모를 삭제하시겠습니까?')) {
            deleteMutation.mutate(memo.id);
        }
    };

    const categoryOptions = [
        { value: '계약', label: '계약' },
        { value: '개장', label: '개장' },
        { value: '석물', label: '석물' },
        { value: '관리비', label: '관리비' },
        { value: '장례', label: '장례' },
        { value: '보수', label: '보수' },
        { value: '기타', label: '기타' }
    ];

    // transactionId 검증
    if (!transactionId) {
        return (
            <div className="error">
                계약 ID를 찾을 수 없습니다.
            </div>
        );
    }

    if (isLoading) {
        return <div className="loading">메모 정보를 불러오는 중...</div>;
    }

    if (error) {
        return (
            <div className="error">
                메모 정보를 불러오는 중 오류가 발생했습니다: {error.message}
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* 헤더 */}
            <div className="flex-between mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(`/contracts/${transactionId}`)}
                        className="btn btn-secondary"
                        style={{ padding: '8px' }}
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">메모 관리</h1>
                        {/* <p className="text-gray-600">
                            계약번호: {transactionId}
                            {contractData && ` - ${contractData.type} (${formatDate(contractData.contract_date)})`}
                        </p> */}
                    </div>
                </div>

                <button
                    onClick={() => setIsCreateModalOpen(true)}
                    disabled={!hasPermission(user?.role, 'MEMO_CREATE')}
                    className={`btn flex items-center gap-2 ${
                        hasPermission(user?.role, 'MEMO_CREATE') 
                            ? 'btn-primary' 
                            : 'btn-secondary opacity-50 cursor-not-allowed'
                    }`}
                    title={!hasPermission(user?.role, 'MEMO_CREATE') ? '메모 추가 권한이 없습니다.' : ''}
                >
                    <Plus size={16} />
                    메모 추가
                </button>
            </div>

            {/* 메모 목록 */}
            <div className="space-y-4">
                {/* 디버깅용 로그 */}
                {console.log('렌더링 시 memoData:', memoData)}
                {console.log('memoData.memos:', memoData?.memos)}
                {console.log('memoData.memos.length:', memoData?.memos?.length)}
                {console.log('memoData.memos 타입:', typeof memoData?.memos)}
                {console.log('memoData.memos가 배열인가?', Array.isArray(memoData?.memos))}
                
                {memoData?.memos && memoData.memos.length > 0 ? (
                    memoData.memos.map((memo) => (
                        <div key={memo.id} className="card">
                            <div className="flex-between">
                                <div className="flex items-center gap-3">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                        memo.category === '계약' ? 'bg-blue-100 text-blue-800' :
                                        memo.category === '개장' ? 'bg-green-100 text-green-800' :
                                        memo.category === '석물' ? 'bg-purple-100 text-purple-800' :
                                        memo.category === '관리비' ? 'bg-yellow-100 text-yellow-800' :
                                        memo.category === '장례' ? 'bg-red-100 text-red-800' :
                                        memo.category === '보수' ? 'bg-orange-100 text-orange-800' :
                                        'bg-gray-100 text-gray-800'
                                    }`}>
                                        {memo.category}
                                    </span>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleEdit(memo)}
                                        disabled={!hasPermission(user?.role, 'MEMO_EDIT')}
                                        className={`btn btn-sm ${
                                            hasPermission(user?.role, 'MEMO_EDIT') 
                                                ? 'btn-outline' 
                                                : 'btn-secondary opacity-50 cursor-not-allowed'
                                        }`}
                                        title={!hasPermission(user?.role, 'MEMO_EDIT') ? '메모 수정 권한이 없습니다.' : ''}
                                    >
                                        <Edit size={14} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(memo)}
                                        disabled={!hasPermission(user?.role, 'MEMO_DELETE')}
                                        className={`btn btn-sm ${
                                            hasPermission(user?.role, 'MEMO_DELETE') 
                                                ? 'btn-danger' 
                                                : 'btn-secondary opacity-50 cursor-not-allowed'
                                        }`}
                                        title={!hasPermission(user?.role, 'MEMO_DELETE') ? '메모 삭제 권한이 없습니다.' : ''}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                            
                            <div className="mt-3">
                                <p className="text-gray-900 whitespace-pre-wrap">{memo.content}</p>
                            </div>

                            <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                                    <div className="flex items-center gap-1">
                                        <Calendar size={12} />
                                        <span>등록일시: {formatDateTime(memo.created_at || memo.createdAt)}</span>
                                        {memo.creator?.name && (
                                            <span className="ml-1">({memo.creator.name})</span>
                                        )}
                                    </div>
                                    {(memo.updated_at || memo.updatedAt) && (memo.updated_at || memo.updatedAt) !== (memo.created_at || memo.createdAt) && (
                                        <div className="flex items-center gap-1">
                                            <Calendar size={12} />
                                            <span>최종 수정: {formatDateTime(memo.updated_at || memo.updatedAt)}</span>
                                            {memo.updater?.name && (
                                                <span className="ml-1">({memo.updater.name})</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="card text-center py-8">
                        <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-500">등록된 메모가 없습니다.</p>
                        <button
                            onClick={() => setIsCreateModalOpen(true)}
                            disabled={!hasPermission(user?.role, 'MEMO_CREATE')}
                            className={`btn mt-4 ${
                                hasPermission(user?.role, 'MEMO_CREATE') 
                                    ? 'btn-primary' 
                                    : 'btn-secondary opacity-50 cursor-not-allowed'
                            }`}
                            title={!hasPermission(user?.role, 'MEMO_CREATE') ? '메모 추가 권한이 없습니다.' : ''}
                        >
                            첫 번째 메모 작성하기
                        </button>
                    </div>
                )}
            </div>

            {/* 메모 생성 모달 */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">새 메모 작성</h3>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    카테고리
                                </label>
                                <select
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {categoryOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    내용
                                </label>
                                <textarea
                                    value={formData.content}
                                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                    rows={4}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="메모 내용을 입력하세요..."
                                />
                            </div>

                            <div>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_important || false}
                                        onChange={(e) => setFormData({ ...formData, is_important: e.target.checked })}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">중요 메모</span>
                                </label>
                            </div>
                        </div>
                        
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setIsCreateModalOpen(false);
                                    setFormData({ category: '계약', content: '', is_important: false });
                                }}
                                className="btn btn-secondary flex-1"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleCreate}
                                disabled={createMutation.isLoading}
                                className="btn btn-primary flex-1"
                            >
                                {createMutation.isLoading ? '생성 중...' : '생성'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 메모 수정 모달 */}
            {isEditModalOpen && editingMemo && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">메모 수정</h3>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    카테고리
                                </label>
                                <select
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {categoryOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    내용
                                </label>
                                <textarea
                                    value={formData.content}
                                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                    rows={4}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="메모 내용을 입력하세요..."
                                />
                            </div>
                            
                            <div>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_important || false}
                                        onChange={(e) => setFormData({ ...formData, is_important: e.target.checked })}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">중요 메모</span>
                                </label>
                            </div>
                        </div>
                        
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setIsEditModalOpen(false);
                                    setEditingMemo(null);
                                    setFormData({ category: '계약', content: '', is_important: false });
                                }}
                                className="btn btn-secondary flex-1"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleUpdate}
                                disabled={updateMutation.isLoading}
                                className="btn btn-primary flex-1"
                            >
                                {updateMutation.isLoading ? '수정 중...' : '수정'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MemoDetail; 